/**
 * The fattening hall — the second room in the game, and the first one that is
 * not the farrowing house.
 *
 * Spec §7 beat 3, *Otto*: *"The fattening hall, vast and hazed. Otto shows her
 * the slat that lifts and the vent that breathes. He is too big for either. He
 * demonstrates rolling — and the empty straw pen at night is hers to roll in."*
 *
 * ── Why a second room was worth what it cost ──────────────────────────────────
 * Two beats in one room told us nothing about which parts of that room were a
 * *room* and which were the *engine*. This one answers it: `HallSolid`, the
 * instancer, the zone lighting rig and the kit conventions all came across
 * unchanged, and the only thing that had to be invented was `Placement.rotZ` —
 * for one leaning board. That is a good result and it is the argument for having
 * built the first room properly.
 *
 * ── Staging ───────────────────────────────────────────────────────────────────
 * She comes in at the near end, in the dark. Ahead of her the alley runs away
 * into three pools of amber and then into haze. **The pool she walks toward
 * contains him.** Everything about the layout below serves that one shot.
 *
 * ── Scale is still the argument ───────────────────────────────────────────────
 * 12 m across, 24 m long, a 3.4 m ceiling, a 2.2 m feed passage. The passage is
 * the most comfortable space in the game so far, and it belongs to the machines.
 * She is 0.32 m tall (D-012). Nothing is shrunk to be convenient.
 *
 * ── It is night, and that is a decision, not a mood ───────────────────────────
 * Five high-bays, **three of them lit** — see `fatteningFixtures`. The spec puts
 * the straw pen at night, a finishing house at night runs on a few night lights,
 * and D-027 established that this hardware is bound by lights per fragment. Art
 * and the frame budget agree here, which is how you know the choice is right. Do
 * not brighten it up so the room "reads": the room reads by its pools, exactly as
 * D-054 says the farrowing hall's far end does.
 */

import * as THREE from 'three';
import { PEN_RUBBLE_SLAB } from '@/models/env/pen-rubble';
import { BROKEN_DECK_REMAINDER_CENTER, BROKEN_DECK_THRESHOLD } from '@/models/env/slat-floor-broken';
import { surface } from '@/models/materials';
import { getAsset } from '@/models/registry';
import { strawClump } from '@/models/env/straw-clump';
import { getZone, type ZonePalette } from '@/render/palette';
import type { Fixture } from '@/render/lighting';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';

/** The zone this room is in. Everything else reads its palette from here. */
export const FATTENING_ZONE_ID = 'fattening';

/** Interior dimensions, metres. The hall runs along Z, like the first one. */
export const FAT_HALL = {
  halfWidth: 6,
  /**
   * 24 m long, and the far half of it is never resolved.
   *
   * At this zone's `haze` of 0.55 the far wall dissolves at about 12 m, so
   * everything past the middle is a suggestion. "Vast" is delivered by the fog,
   * not by geometry — `farrowing.ts` said so about 20 m and it is more true here.
   */
  halfLength: 12,
  /** A finishing house is taller than a farrowing house. A real number. */
  ceiling: 3.4,
  /**
   * Half-width of the central feed passage: 2.2 m of it.
   *
   * The farrowing hall's walkway is 1.4 m — wide enough for a person and a hose
   * and no wider. This is wide enough to drive down. It is the only generous
   * space in the game so far and it was built for a machine.
   */
  alley: 1.1,
  wallThickness: 0.2,
} as const;

/**
 * Pen pitch along Z. **Deliberately the farrowing hall's `BAY_PITCH`.**
 *
 * The two buildings were put up by the same people out of the same catalogue, so
 * the modules are the same castings. Keeping the number identical is not a
 * shortcut — it is the reason the whole farm reads as one place.
 */
export const PEN_PITCH = 2.4;

/** Thickness of a `pen-wall` module, and the height of its rail. */
const PEN_WALL_THICKNESS = 0.1;
const PEN_WALL_HEIGHT = 1.05;

/** Centre-line X of the alley-side pen wall. */
const PEN_WALL_X = FAT_HALL.alley + PEN_WALL_THICKNESS / 2;
/** Inboard face of that wall — where a pen's interior starts. */
const PEN_INNER_X = FAT_HALL.alley + PEN_WALL_THICKNESS;
/** Pen depth: exactly two 2.4 m wall modules butted end to end. */
const PEN_DEPTH = 4.8;

/**
 * The slat deck, and the height of every pen floor.
 *
 * Real slats are laid over a slurry channel, so a pen floor is genuinely a step
 * up from the passage. Anything placed in a pen has to know this or it sinks.
 * Same number as the farrowing hall, same reason, same castings.
 */
export const DECK_Y = 0.09;

/** Where the pens start and stop along Z, leaving a cross-passage at each end. */
const PEN_RUN = PEN_PITCH * 4.5;

/**
 * Z centres of the pens: nine per side, and **one of them is exactly zero.**
 *
 * That is why there are nine and not ten. Otto and the empty pen face each other
 * across the alley at z = 0, and having them share a centre-line is the only
 * staging in this beat that is load-bearing — it is what puts the animal who
 * cannot and the animal who can into one frame during the roll.
 */
export function penCentres(): number[] {
  const out: number[] = [];
  for (let k = -4; k <= 4; k += 1) out.push(k * PEN_PITCH);
  return out;
}

/** Z lines of the pen dividers: one at every pen boundary, both outer ends included. */
function dividerLines(): number[] {
  const out: number[] = [];
  for (let k = -4.5; k <= 4.5; k += 1) out.push(k * PEN_PITCH);
  return out;
}

/**
 * The interior of a pen, as world XZ bounds.
 *
 * Beat 3 asks two spatial questions every fixed step — is she in the hospital
 * pen, and how far has she rolled while she was — and both have to be answered
 * against the *same* numbers the walls and the slats were built from. Deriving
 * them again in `main.ts` from `PEN_PITCH` and an assumed alley width is exactly
 * the trap D-056 and D-059 both fell into, one room earlier: two lists built
 * from the same constants, a few hundred lines apart, that drift the day one of
 * them is edited.
 *
 * So it is a function here, next to the constants, and there is one of it.
 */
export function penBounds(pen: PenAddress): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  const near = pen.side * PEN_INNER_X;
  const far = pen.side * (PEN_INNER_X + PEN_DEPTH);
  return {
    minX: Math.min(near, far),
    maxX: Math.max(near, far),
    minZ: pen.z - PEN_PITCH / 2,
    maxZ: pen.z + PEN_PITCH / 2,
  };
}

/** A pen, identified the way the beat talks about it. */
export interface PenAddress {
  side: -1 | 1;
  z: number;
}

/**
 * Otto's pen. Right of the alley, on the centre-line.
 *
 * He is 2.35 m long and the pen is 2.40 m across. **He barely fits, and it is
 * arithmetic rather than staging** — the same trick `sow.ts` plays with the
 * crate lane, working for the same reason: the player is never told, they just
 * see a body longer than the space it is in. He stands along Z so his full
 * length is in profile from the alley, with 25 mm at his nose and 25 mm at his
 * rump.
 */
export const OTTO_PEN: PenAddress = { side: 1, z: 0 };
/** Where he stands in it, facing down the hall. */
export const OTTO_AT = { x: PEN_INNER_X + 1, y: DECK_Y, z: 0 } as const;

/**
 * The broken corner is deliberate arithmetic: the 0.52 m top leaves Rosalie
 * 0.43 m above the 0.09 m deck while denying Otto's 1.06 m shoulder. Keeping
 * these bounds exported makes the later traversal gate measure this exact hole
 * instead of silently substituting an easier opening.
 */
export const OTTO_GAP = {
  /** Z offsets from the pen centre. */
  fromZ: -1.08,
  toZ: -0.58,
  /** Top of the hole, above the ALLEY floor. The deck inside is at DECK_Y. */
  top: 0.52,
} as const;

/**
 * The middle of the hole, in world XZ — what Otto turns his head to.
 *
 * `OttoDirector` is handed this rather than computing it, and beat 3's part
 * (`src/game/beats/otto.ts`) passes it straight through without arithmetic of its own. He is pointing at the only
 * thing in the beat she can act on, and if this and the hole ever disagreed he
 * would be inviting her through a slab of concrete.
 */
export const OTTO_GAP_AT = {
  x: OTTO_PEN.side * PEN_WALL_X,
  z: OTTO_PEN.z + (OTTO_GAP.fromZ + OTTO_GAP.toZ) / 2,
} as const;

/**
 * The missing slats continue the broken corner into the pen. The wall opening
 * is narrow, so its 90 mm step has to begin beyond the jambs where the capsule
 * can roll over it; making collision permissive while leaving whole slats drawn
 * there was the rejected D-149 shortcut.
 */
export const OTTO_DECK_BREAK = {
  fromX: PEN_INNER_X,
  toX: PEN_INNER_X + BROKEN_DECK_THRESHOLD,
  fromZ: -PEN_PITCH / 2,
  toZ: 0,
} as const;

/**
 * Where the piece that came out is lying, and it is one transform for two jobs.
 *
 * `dressFatteningHall` places `pen-rubble` here and `fatteningSolids` builds its
 * collider from the same values, which is `BLACKOUT_OTTO_GATE_AT`'s pattern a
 * few hundred lines down and this room's answer to D-056 and D-059.
 *
 * **It sits downstream of the hole, not in front of it.** She comes up the alley
 * from −Z, so anything in `OTTO_GAP`'s own span would be an obstacle at the one
 * threshold this revision exists to open; the slab's rotated footprint reaches
 * about 0.24 m either side of its centre, which is what puts that centre at
 * −0.30 rather than hard against the break.
 *
 * A gap in a wall is ambiguous; a gap with the piece that came out of it lying
 * beside it is not. The hospital pen two and a bit metres away says the same
 * thing with a whole module propped against its neighbour — same grammar,
 * opposite cause: that one is disuse and this one is decay.
 */
export const OTTO_CHUNK_AT = {
  x: OTTO_PEN.side * (PEN_WALL_X - 0.30),
  z: OTTO_PEN.z - 0.30,
  rotY: 0.3,
} as const;

/**
 * The empty pen. Left of the alley, facing Otto, and it is the point of the beat.
 *
 * Spec §5.4 requires **one consequence-free rolling space** in Act 1, before
 * stealth pressure teaches the player never to touch the game's best-feeling
 * verb. This is it.
 *
 * **It is the hospital pen**, which is why it is bedded with straw in a building
 * where every other floor is slats: a real unit straws the pen it puts sick
 * animals in. So the only soft, warm, joyful place in this hall is the pen for
 * animals that are not going to make it. Nothing says that out loud and nothing
 * should.
 */
export const STRAW_PEN: PenAddress = { side: -1, z: 0 };

/**
 * The one fitting in this hall that is not a high-bay, hung over the hospital pen.
 *
 * **The important place had to be lit, and it had to be lit for a reason.** Under
 * the row of sodium alone the straw sat four metres off the alley centre-line and
 * came out grey — the pen the beat is *for* was the dimmest thing in the frame,
 * which is a staging failure, not a lighting one.
 *
 * A hospital pen having its own lamp is simply true: it is the pen somebody has
 * to come and look into. So the room's answer and the story's answer are the same
 * answer, and it costs one point light. It is the same brooder fitting the
 * farrowing bay uses — same farm, same catalogue — and it is the only warm light
 * in either building that is there for an animal rather than for a worker. That
 * irony was beat 1's and it is this beat's too.
 */
const INSPECTION_LAMP_AT = { x: -2.7, y: 2.15, z: 0 } as const;

/**
 * Where the pens are lit from: five sodium high-bays down the alley centre-line.
 *
 * 4.8 m apart, which is two pens. The gaps are the point — the passage is a
 * rhythm of pools and dark, and at night most of it is dark.
 */
const LAMP_Z: readonly number[] = [-9.6, -4.8, 0, 4.8, 9.6];
/**
 * The three that are switched on. The middle three, and the choice is the shot.
 *
 * She arrives in an unlit cross-passage and the hall opens ahead of her into
 * three receding pools. The far end is dark, which is where the vent is, which is
 * beat 4's business.
 */
const LAMP_LIT: readonly number[] = [-4.8, 0, 4.8];

/**
 * The fourth one, and it is switched on by beat 3 rather than by the room.
 *
 * ── Why the far end stops being dark ────────────────────────────────────────
 *
 * Beat 3's last stage is a walk to the vent in the far wall, and the vent is
 * **around the corner in the far cross-passage** — the pen run reaches z = 10.8
 * and the vent sits at x = −3.4, so from anywhere in the alley it is behind a
 * block of pens and cannot be seen at all. Otto turning his head at it is
 * likewise invisible: he is behind a 1.05 m pen wall (D-071). So the beat's
 * closing instruction had no line of sight to the player from any position she
 * could be standing in, which is why it read as no instruction. D-087.
 *
 * A pool of light at the end of a hall that has been dark for the whole beat is
 * the one thing this room can say at twenty-four metres through haze. It is not
 * new machinery — it is the fourth of the five fittings that are already hanging
 * there, and the player has spent the beat learning that a lit bay is somewhere
 * you can be.
 *
 * It stays dark for everything else, including `?beat=0`: the authored night
 * shift is three pools and a dark end, and only the beat's last stage is allowed
 * to change that. `ottoDirector.ts` owns it and puts it back on dispose.
 */
export const FAR_LAMP_NAME = 'fattening-far-high-bay';
const LAMP_FAR_Z = 9.6;

/**
 * What a lit high-bay delivers. Exported so the beat that raises the far one
 * asks for the room's own number instead of carrying a copy of it.
 *
 * 16 rather than the farrowing hall's 6.5, and the range (11) is deliberately
 * SHORTER than that room's relative to the space. First build ran 9 over a range
 * of 13 and the hall came out uniformly lit — every pool overlapped its
 * neighbours into one flat wash, which is the exact opposite of a night shift. A
 * high-bay is a hard downward cone, not a room light: it has to be bright enough
 * to blow out what is under it and to reach nothing three metres away, or the
 * gaps between the pools are not dark and there is no rhythm to move through.
 */
export const HIGH_BAY_INTENSITY = 16;

/**
 * Where she comes in: the head of the alley, by the first pen, facing down the hall.
 *
 * Facing is authored (D-058) rather than left to default. It is read by the
 * interaction cone, the movement basis and the camera's initial yaw, and only the
 * cone ever complains.
 *
 * **X was −1.3 and −1.3 was inside the pen block.** The alley is only ±1.1 wide,
 * so she stood 0.45 m in front of the end wall of the first pen with her authored
 * facing pointing straight at it: hold forward from the spawn and she covered
 * 0.26 m in nine seconds and stopped. The room shipped like that and nothing
 * caught it, because a room that is only ever *looked at* never walks anywhere —
 * it took beat 3's first stage, which is literally "walk into the hall", to ask
 * the question. A spawn is a promise that forward is a direction.
 *
 * **Z was −11.25, 0.75 m off the end wall** (D-209, audit rows 6 and 11). The
 * spring arm folded against the wall to 0.55 m and pitched down onto her back:
 * the first frame of beat 3 and of beat 8 part 2 was her dark silhouette on a
 * dark floor (luminance spread 2.4 %), and the three lit pools the room is
 * built around were out of shot. At −9.3 the arm opens and the first pool is
 * in the opening frame — the way on is the lit thing.
 */
export const FATTENING_SPAWN = { x: -0.6, y: 0.25, z: -9.3, facing: 0 } as const;

/** The return starts at the same real door, rather than inventing a second hall entrance. */
export const BLACKOUT_HALL_SPAWN = FATTENING_SPAWN;
/** The returned gate swings into Otto's old pen instead of closing the alley mouth. */
export const BLACKOUT_OTTO_GATE_AT = {
  x: PEN_INNER_X + PEN_PITCH / 2,
  y: DECK_Y,
  // The +Z hinge leaves the mouth centre clear. The turned panel now lies inside
  // the pen instead of cutting across the doorway it is meant to have opened.
  z: OTTO_PEN.z + PEN_PITCH / 2 - PEN_WALL_THICKNESS / 2,
} as const;
/** The live kit has its lever at y=0.30; this alley-side service wall keeps it reachable. */
export const BLACKOUT_FUSE_AT = { x: FAT_HALL.alley - 0.06, y: 0, z: PEN_PITCH * 2 } as const;
export const BLACKOUT_FUSE_INTERACT_AT = { x: FAT_HALL.alley - 0.18, y: 0.30, z: BLACKOUT_FUSE_AT.z } as const;
export const BLACKOUT_PASS_PEN_Z = PEN_PITCH * 0.7;

/**
 * The crates, and the arithmetic in them is the spec's second sentence.
 *
 * Spec §7.8: *"Free the hens. They scatter uselessly, and some do not make it."*
 * There is **no counter and no tally** (spec §9, and the plan says so twice), so
 * "some do not make it" has to be delivered by the shape of the thing rather
 * than by a number — which means it has to be delivered by geometry, because
 * geometry is the only thing in this room that can state a fact without saying
 * it.
 *
 * Six crates, in a row along the alley's −X side where a lorry would come for
 * them. Four stand on the floor; two more are stacked on the far pair. The lids
 * hinge at their rear rim, so **a crate with another crate on it cannot open**,
 * and a lid at 0.565 m is over the head of an animal whose whole beat is that a
 * fuse box at boot height is at head height for her.
 *
 * So she can open exactly two of the six. That is not a limitation that was
 * worked around; it is the sentence. The four she cannot reach keep making
 * noise for the rest of the beat.
 */
const CRATE_X = -FAT_HALL.alley + 0.55 / 2;
/** 0.75 m of crate and a 50 mm gap, which is how a stack of them is actually left. */
const CRATE_PITCH = 0.80;
/** Measured off `hen-crate`: the interlock sockets, not the lid, carry the crate above. */
const CRATE_STACK_Y = 0.295;
/** Four on the floor, starting past the fuse box so the alley reads clear on the approach. */
const CRATE_ROW_Z: readonly number[] = [5.85, 5.85 + CRATE_PITCH, 5.85 + CRATE_PITCH * 2, 5.85 + CRATE_PITCH * 3];

/** Every crate in the returned hall, floor row first. `rotY` lays the 0.75 m face along Z. */
export function blackoutCratePlacements(): Placement[] {
  const placements: Placement[] = CRATE_ROW_Z.map((z) => ({ x: CRATE_X, y: 0, z, rotY: Math.PI / 2 }));
  // Stacked on the far pair, which is what puts their lids out of her reach.
  for (const z of CRATE_ROW_Z.slice(2)) {
    placements.push({ x: CRATE_X, y: CRATE_STACK_Y, z, rotY: Math.PI / 2 });
  }
  return placements;
}

/**
 * The two she can open, in the order she meets them walking +Z.
 *
 * Exported as the single source both the dressing and the interaction read, for
 * the reason `penBounds` exists: a second list of "which crates open" written in
 * `main.ts` is D-056 and D-059 happening a third time.
 */
export const BLACKOUT_OPEN_CRATES: readonly { readonly index: number; readonly z: number }[] = [
  { index: 0, z: CRATE_ROW_Z[0] ?? 0 },
  { index: 1, z: CRATE_ROW_Z[1] ?? 0 },
];

/** The lid hinge in world space, so the flock and the lid animation share one number. */
export function blackoutCrateLidHinge(index: number): { x: number; y: number; z: number } {
  const z = BLACKOUT_OPEN_CRATES[index]?.z ?? 0;
  // The asset hinges at its local rear rim (0, 0.27, −0.255); the quarter turn
  // that lays it along Z sends that −Z offset to −X, against the hall wall.
  return { x: CRATE_X - 0.255, y: 0.27, z };
}

/** Where she stands to reach a lid, and it is the alley side rather than the wall side. */
export function blackoutCrateInteractAt(index: number): { x: number; y: number; z: number } {
  return { x: CRATE_X + 0.24, y: 0.27, z: BLACKOUT_OPEN_CRATES[index]?.z ?? 0 };
}

/** A redress state, deliberately separate from beat numbers so the hall stays reusable. */
export interface FatteningHallOptions {
  readonly returnState?: boolean;
}

/** The one closed mouth a returned hall opens; shared by mesh and collider lists. */
export function isFatteningMouthOpen(options: FatteningHallOptions, side: -1 | 1, z: number): boolean {
  return options.returnState === true && side === OTTO_PEN.side && z === OTTO_PEN.z;
}

/**
 * The vent that breathes — spec §7 beat 3, and now beat 3's way out.
 *
 * It was placed in session 17 as something Otto could look at and nothing more,
 * with a comment saying it was beat 4's exit and was filed there. **It is used
 * now**, because the beat needed an ending that was an action rather than a
 * clock: Alex, playing the act in order, *"there is not really anything to do to
 * progress and then Rosalie is suddenly in the next room."*
 *
 * Exported as a constant rather than left inline in the dressing for the reason
 * every other placement in this file is: the beat's flag and the mesh must be
 * the same hole in the same wall.
 */
export const VENT_AT = { x: -3.4, z: FAT_HALL.halfLength - 0.03 } as const;

/** How close she has to get for the vent to be the way out. A snout's reach, not a trigger volume. */
export const VENT_REACH = 1.6;

// Resolved in an IIFE for the reason `farrowing.ts` gives: a module-level `if`
// guard reads as proof to a human and as nothing at all to the checker.
const zone: ZonePalette = (() => {
  const found = getZone(FATTENING_ZONE_ID);
  if (!found) throw new Error(`fattening zone missing from the palette`);
  return found;
})();

// Albedo by index into the zone's own surface list, darkest to lightest. Fresh
// hex literals here would be this room quietly leaving its own palette.
const CONCRETE_DARK = zone.surfaces[0] ?? 0x1d3033;
const CONCRETE_FLOOR = zone.surfaces[1] ?? 0x4a4038;
const CONCRETE_WALL = zone.surfaces[2] ?? 0x6e7476;
const STEEL = zone.surfaces[4] ?? 0xe8b96a;

/**
 * Every box in the hall shell, mesh and collider alike. See `solids.ts`.
 *
 * The pen walls are in here as **collision-only** entries: they are already
 * drawn, better, by the instanced `pen-wall` kit in `dressFatteningHall`, and
 * boxing them again would spend draw calls hiding a good mesh behind a worse one.
 * The two lists are built from the same constants a few lines apart, which is the
 * closest this room gets to the trap D-056 and D-059 both fell into.
 */
/**
 * The fallen slab's collider, from the asset's own dimensions and the room's own
 * transform — the two things that also draw it.
 *
 * It is axis-aligned and the slab is not, so the half-extents are the rotated
 * box's bounding box rather than the box. Taking the unrotated size instead
 * leaves a collider smaller than the thing you can see, and the corner she can
 * stand inside is the corner nearest the hole.
 *
 * She does not have to go round it. It is 0.06 m of concrete against an autostep
 * of 0.26, so she steps onto it and off it — the collider is here to stop her
 * walking *through* the only object in the alley, not to make an obstacle.
 */
function ottoChunkSolid(): HallSolid {
  const c = Math.abs(Math.cos(OTTO_CHUNK_AT.rotY));
  const s = Math.abs(Math.sin(OTTO_CHUNK_AT.rotY));
  const halfY = PEN_RUBBLE_SLAB.y / 2 + 0.01;
  return {
    name: 'otto-fallen-chunk',
    half: {
      x: (PEN_RUBBLE_SLAB.x / 2) * c + (PEN_RUBBLE_SLAB.z / 2) * s,
      y: halfY,
      z: (PEN_RUBBLE_SLAB.x / 2) * s + (PEN_RUBBLE_SLAB.z / 2) * c,
    },
    pos: { x: OTTO_CHUNK_AT.x, y: halfY, z: OTTO_CHUNK_AT.z },
    colour: CONCRETE_WALL,
    kind: 'mineral',
    visible: false,
  };
}

export function fatteningSolids(options: FatteningHallOptions = {}): HallSolid[] {
  const solids: HallSolid[] = [];
  const { halfWidth, halfLength, ceiling, wallThickness } = FAT_HALL;

  // One slab under everything. Unlike the farrowing hall there is no channel down
  // the passage to walk into: a finishing house keeps its slurry under the slats,
  // where it belongs, and the drain trap that cost session 8 a review item simply
  // does not exist here.
  solids.push({
    name: 'floor',
    half: { x: halfWidth, y: 0.1, z: halfLength },
    pos: { x: 0, y: -0.1, z: 0 },
    colour: CONCRETE_FLOOR,
    kind: 'mineral',
  });

  // The pen decks, as collision only — the slat tiles and the straw pen's own
  // deck draw them. One box per side covering the pen run; the cross-passages at
  // each end stay at floor level, which is what makes the decks read as a step up.
  for (const side of [-1, 1] as const) {
    if (side === OTTO_PEN.side && !options.returnState) {
      const deckMinX = PEN_INNER_X;
      const deckMaxX = PEN_INNER_X + PEN_DEPTH;
      const deckMinZ = -PEN_RUN;
      const deckMaxZ = PEN_RUN;
      const addDeck = (name: string, minX: number, maxX: number, minZ: number, maxZ: number): void => {
        solids.push({
          name, half: { x: (maxX - minX) / 2, y: DECK_Y / 2, z: (maxZ - minZ) / 2 },
          pos: { x: (minX + maxX) / 2, y: DECK_Y / 2, z: (minZ + maxZ) / 2 },
          colour: CONCRETE_FLOOR, kind: 'mineral', visible: false,
        });
      };
      // Three honest solids around the same recess the broken tile shows.
      addDeck('deck-r-otto-main', OTTO_DECK_BREAK.toX, deckMaxX, deckMinZ, deckMaxZ);
      addDeck('deck-r-otto-near', deckMinX, OTTO_DECK_BREAK.toX, deckMinZ, OTTO_DECK_BREAK.fromZ);
      addDeck('deck-r-otto-far', deckMinX, OTTO_DECK_BREAK.toX, OTTO_DECK_BREAK.toZ, deckMaxZ);
      continue;
    }
    solids.push({
      name: `deck-${side > 0 ? 'r' : 'l'}`,
      half: { x: PEN_DEPTH / 2, y: DECK_Y / 2, z: PEN_RUN },
      pos: { x: side * (PEN_INNER_X + PEN_DEPTH / 2), y: DECK_Y / 2, z: 0 },
      colour: CONCRETE_FLOOR,
      kind: 'mineral',
      visible: false,
    });
  }

  // Side walls.
  for (const side of [-1, 1] as const) {
    solids.push({
      name: `wall-${side > 0 ? 'r' : 'l'}`,
      half: { x: wallThickness / 2, y: ceiling / 2, z: halfLength },
      pos: { x: side * (halfWidth + wallThickness / 2), y: ceiling / 2, z: 0 },
      colour: CONCRETE_WALL,
      kind: 'mineral',
    });
  }

  // End walls. The far one is the vanishing point of every shot down the alley
  // and is the darkest surface in the room, for the reason the farrowing hall's
  // is: the eye is meant to travel to it and find nothing.
  for (const [i, side] of ([-1, 1] as const).entries()) {
    solids.push({
      name: `end-${side > 0 ? 'far' : 'near'}`,
      half: { x: halfWidth + wallThickness, y: ceiling / 2, z: wallThickness / 2 },
      pos: { x: 0, y: ceiling / 2, z: side * (halfLength + wallThickness / 2) },
      colour: i === 1 ? CONCRETE_DARK : CONCRETE_WALL,
      kind: 'mineral',
    });
  }

  solids.push({
    name: 'ceiling',
    half: { x: halfWidth + wallThickness, y: 0.1, z: halfLength + wallThickness },
    pos: { x: 0, y: ceiling + 0.1, z: 0 },
    colour: CONCRETE_DARK,
    kind: 'mineral',
  });

  // ── the pen walls, collision only ──────────────────────────────────────────
  //
  // Every one of them is collided, including the ones deep in pens she cannot
  // reach this beat. That is deliberate: the alternative is a room whose solidity
  // depends on which beat is running, and the first time a later beat opens a
  // second pen the omission would present as "she walks through a wall" rather
  // than as a missing collider. Sixty static cuboids cost Rapier nothing.
  const wallHalfY = PEN_WALL_HEIGHT / 2;
  for (const side of [-1, 1] as const) {
    // The alley-side wall of each pen — except the one that has been lifted out.
    for (const z of penCentres()) {
      if (side === STRAW_PEN.side && z === STRAW_PEN.z) continue;
      if (side === OTTO_PEN.side && z === OTTO_PEN.z && !options.returnState) {
        // ── Otto's mouth is three boxes, and the hole between them is the beat ──
        //
        // The alternative is what every other pen here has — one invisible panel
        // the full 2.4 m — and it would leave the kit promising an entrance the
        // controller does not have. A mesh she can see through and cannot walk
        // through is the failure this room is most likely to ship, because the
        // dressing and the collision are built in two loops a few hundred lines
        // apart (D-056, D-059).
        //
        // **Every Z here is an offset from the pen centre `z`, not a world
        // coordinate.** Otto's pen happens to sit at z = 0, so dropping the `z +`
        // is invisible today and wrong the day his pen moves — which the beat 8
        // re-dress has already demonstrated this room is willing to do.
        //
        // The gap the three boxes leave is `OTTO_GAP`: 0.50 m of Z between the
        // surviving end post and the remaining panel, open from the alley floor
        // to 0.52 m.
        //
        // **Above 0.52 the collider is solid where the mesh is not**, because the
        // lintel is one box and the panel is three stepped courses. That is
        // deliberate and it is the safe direction — collision is never more
        // permissive than the picture — but it is a real disagreement and not a
        // claim of fidelity: from about z −1.08 to −0.90 you can see through
        // concrete she cannot pass. She is 0.32 m tall and none of it is
        // reachable, which is the whole reason it is tolerated. `otto.spec.ts` walks her through it and walks her into the
        // pen one bay along with the same input to prove the other one refuses.
        const mouth = `pen-wall-${side > 0 ? 'r' : 'l'}-${z}`;
        solids.push(
          {
            name: `${mouth}-post`,
            half: { x: PEN_WALL_THICKNESS / 2, y: wallHalfY, z: (OTTO_GAP.fromZ + PEN_PITCH / 2) / 2 },
            pos: { x: side * PEN_WALL_X, y: wallHalfY, z: z + (OTTO_GAP.fromZ - PEN_PITCH / 2) / 2 },
            colour: CONCRETE_WALL,
            kind: 'mineral',
            visible: false,
          },
          {
            name: `${mouth}-lintel`,
            half: { x: PEN_WALL_THICKNESS / 2, y: (PEN_WALL_HEIGHT - OTTO_GAP.top) / 2, z: (OTTO_GAP.toZ - OTTO_GAP.fromZ) / 2 },
            pos: { x: side * PEN_WALL_X, y: OTTO_GAP.top + (PEN_WALL_HEIGHT - OTTO_GAP.top) / 2, z: z + (OTTO_GAP.fromZ + OTTO_GAP.toZ) / 2 },
            colour: CONCRETE_WALL,
            kind: 'mineral',
            visible: false,
          },
          {
            name: `${mouth}-main`,
            half: { x: PEN_WALL_THICKNESS / 2, y: wallHalfY, z: (PEN_PITCH / 2 - OTTO_GAP.toZ) / 2 },
            pos: { x: side * PEN_WALL_X, y: wallHalfY, z: z + (PEN_PITCH / 2 + OTTO_GAP.toZ) / 2 },
            colour: CONCRETE_WALL,
            kind: 'mineral',
            visible: false,
          },
          ottoChunkSolid(),
        );
        continue;
      }
      if (isFatteningMouthOpen(options, side, z)) continue;
      solids.push({
        name: `pen-wall-${side > 0 ? 'r' : 'l'}-${z}`,
        half: { x: PEN_WALL_THICKNESS / 2, y: wallHalfY, z: PEN_PITCH / 2 },
        pos: { x: side * PEN_WALL_X, y: wallHalfY, z },
        colour: CONCRETE_WALL,
        kind: 'mineral',
        visible: false,
      });
    }
    // The dividers between pens, standing on the deck, two modules deep.
    for (const z of dividerLines()) {
      solids.push({
        name: `pen-divider-${side > 0 ? 'r' : 'l'}-${z}`,
        half: { x: PEN_DEPTH / 2, y: wallHalfY, z: PEN_WALL_THICKNESS / 2 },
        pos: { x: side * (PEN_INNER_X + PEN_DEPTH / 2), y: DECK_Y + wallHalfY, z },
        colour: CONCRETE_WALL,
        kind: 'mineral',
        visible: false,
      });
    }
  }

  if (options.returnState) {
    // The same open-gate transform drives picture and collision: an opened mouth
    // is not permission to ghost through the panel now lying across the pen.
    solids.push({
      name: 'blackout-otto-open-gate-solid',
      half: { x: PEN_PITCH / 2, y: PEN_WALL_HEIGHT / 2, z: PEN_WALL_THICKNESS / 2 },
      pos: { x: BLACKOUT_OTTO_GATE_AT.x, y: DECK_Y + PEN_WALL_HEIGHT / 2, z: BLACKOUT_OTTO_GATE_AT.z },
      colour: CONCRETE_WALL,
      kind: 'mineral',
      visible: false,
    });

    // The crates are solid, and they are solid off the SAME list the dressing
    // instances. A stack she can walk through is a stack she can walk through
    // while Kestner's torch is on the alley, which is the one moment in this
    // beat where the difference between picture and collision would be the
    // whole scene.
    for (const [index, crate] of blackoutCratePlacements().entries()) {
      const base = crate.y ?? 0;
      solids.push({
        name: `blackout-hen-crate-solid-${String(index)}`,
        // The quarter turn in the placement lays the 0.75 m face along Z, so the
        // half extents are swapped here rather than the box being rotated.
        half: { x: 0.55 / 2, y: 0.30 / 2, z: 0.75 / 2 },
        pos: { x: crate.x, y: base + 0.30 / 2, z: crate.z },
        colour: CONCRETE_WALL,
        kind: 'mineral',
        visible: false,
      });
    }
  }

  // The straw pen's own floor: concrete, not slats, because it is the hospital
  // pen. Drawn as well as solid — nothing else covers it.
  solids.push({
    name: 'straw-pen-deck',
    half: { x: PEN_DEPTH / 2, y: DECK_Y / 2, z: PEN_PITCH / 2 },
    pos: { x: STRAW_PEN.side * (PEN_INNER_X + PEN_DEPTH / 2), y: DECK_Y / 2, z: STRAW_PEN.z },
    colour: CONCRETE_DARK,
    kind: 'mineral',
  });

  return solids;
}

/**
 * The practicals: sodium high-bays, and only three of them are on.
 *
 * **This function returns the LIT ones.** All five fittings are drawn by the
 * dressing; two of them are dark objects hanging in a dark hall, which is what a
 * finishing house looks like at night and is also two point lights this frame
 * does not pay for (D-027).
 *
 * A high-bay is a point source, not a batten: it is a single lamp in a reflector
 * throwing a cone at the floor, and the pool it makes is the thing the staging is
 * built on.
 */
/**
 * The one light in this hall a beat is allowed to touch.
 *
 * Named so `ottoDirector.ts` can find it again (D-087). Everything else in the
 * room's lighting is a fixed design and stays that way — see the `name` field on
 * `Fixture` for why naming a light is close to a last resort.
 */
export const INSPECTION_LAMP_NAME = 'fattening-inspection-lamp';

export function fatteningFixtures(): Fixture[] {
  const inspection: Fixture = {
    kind: 'point',
    name: INSPECTION_LAMP_NAME,
    // Below the fitting, not level with it: these point lights cast no shadow, so
    // a fitting cannot occlude its own light and the emitter has to be moved.
    position: { x: INSPECTION_LAMP_AT.x, y: INSPECTION_LAMP_AT.y - 0.16, z: INSPECTION_LAMP_AT.z },
    // Warmer and softer than the sodium, and deliberately a different warm — two
    // shades of amber is a room with two kinds of light in it, which is what
    // makes the pen read as somewhere else rather than as more hall.
    colour: 0xffc98a,
    // 8 over 5.5 m, and both were retuned upward the day this lamp stopped being
    // atmosphere and became the beat's only pointer (D-087).
    //
    // At 8 the pool did not clear the 1.05 m pen wall, so from the alley — which
    // is the ONLY place the player watches this from, because D-071 says she
    // cannot see over that wall — switching it on and off changed the hall by
    // about one value step. Two screenshots taken a stage apart were hard to tell
    // apart, and a pointer nobody can see is the bug this was supposed to fix.
    // 15 over 7 m puts warm light on the far wall behind the pen and on the
    // propped panel that is the way in, which is what the player actually sees.
    intensity: 15,
    range: 7,
  };
  // Same fitting, same numbers, at the fourth position — written by mapping the
  // lit list over `LAMP_FAR_Z` as well rather than as a hand-copied literal, so
  // retuning a high-bay retunes all four of them. See `FAR_LAMP_NAME`.
  const highBay = (z: number, name?: string, intensity = HIGH_BAY_INTENSITY): Fixture => ({
    kind: 'point',
    // 0.55 m below the ceiling — the mouth of the reflector rather than the
    // fitting's mounting point, for the reason `farrowing.ts` gives about its
    // battens: a point light at ceiling height paints a soft ellipse on the
    // ceiling above the lamp that reads unmistakably as a game engine.
    position: { x: 0, y: FAT_HALL.ceiling - 0.55, z },
    intensity,
    range: 11,
    ...(name === undefined ? {} : { name }),
  });

  return [
    ...LAMP_LIT.map((z) => highBay(z)),
    // ── Authored DARK, and it must stay that way ────────────────────────────
    //
    // The first version of this shipped the far bay at full intensity and left
    // `ottoDirector.ts` to switch it off at construction. That director only
    // exists during beat 3 — so `?room=fattening` with no beat, every
    // turnaround, and every other beat got a brightly lit far end, which is the
    // exact opposite of the authored night shift and of what the comment beside
    // it claimed. Caught by an independent review, not by a gate: the budget
    // counts lights, not their intensity, and no screenshot in the suite frames
    // that end of the hall.
    //
    // **The room's authored state is the dark one.** A beat may raise this; a
    // beat that forgets to put it back leaves the hall as designed rather than
    // wrong, which is the safe direction for the default to fail in.
    highBay(LAMP_FAR_Z, FAR_LAMP_NAME, 0),
    inspection,
  ];
}

/**
 * The machinery you can hear, and where it is. Spec §8: no score inside the farm.
 *
 * Two sources, and the second is the one worth having:
 *
 * - **The extractor is in the far wall**, so the hall has a direction and the eye
 *   and the ear agree about which way onward is. Same trick as the first room,
 *   and it works because it is the same building.
 * - **The slurry pump is UNDER THE FLOOR**, at negative Y, in the middle of the
 *   hall. In the farrowing house it was beyond the far wall and unlocatable — a
 *   hint that the building continues. Here it is directly beneath the pens she is
 *   walking past, which is a different and worse fact about where she is.
 *
 * No fluorescent buzz: there are no fluorescents in this room. The temptation to
 * carry it over "because the hall sounds empty without it" is the temptation to
 * make the second room a copy of the first one.
 */
export function fatteningAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [
    { id: 'extractor-fan', at: { x: 2.6, y: 2.5, z: FAT_HALL.halfLength + 0.2 }, gain: 0.75 },
    { id: 'slurry-pump', at: { x: 3.6, y: -0.4, z: 2.4 }, gain: 0.5 },
  ];
}

function assetOrThrow(id: string): THREE.Group {
  const asset = getAsset(id);
  if (!asset) throw new Error(`fattening hall: asset "${id}" is not registered`);
  return asset.create();
}

/**
 * Ceiling beams and the wall panel joint. Dressing, and it is load-bearing.
 *
 * A hall of flat planes reads as an untextured box however well it is shaded —
 * there is nothing in it for the eye to measure distance against, and in a room
 * this size that is fatal to the one thing it is for. Beams on the pen pitch give
 * the ceiling a rhythm that recedes.
 */
function structure(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'structure';

  const beamGeometry = new THREE.BoxGeometry((FAT_HALL.halfWidth + FAT_HALL.wallThickness) * 2, 0.2, 0.12);
  const beam = new THREE.Mesh(beamGeometry, surface('mineral', CONCRETE_WALL));
  beam.name = 'beam';
  beam.castShadow = true;
  beam.receiveShadow = true;
  beam.position.y = FAT_HALL.ceiling - 0.1;
  g.add(instanceGroup(beam, dividerLines().map((z) => ({ x: 0, z })), 'ceiling-beams'));

  // The joint between the washable lower wall panel and the wall above it, at the
  // height a pig can reach. A free horizontal to read the length of the hall by.
  const bandGeometry = new THREE.BoxGeometry(0.03, 0.05, FAT_HALL.halfLength * 2);
  for (const side of [-1, 1] as const) {
    const band = new THREE.Mesh(bandGeometry, surface('metal', STEEL));
    band.position.set(side * (FAT_HALL.halfWidth - 0.015), 1.25, 0);
    band.name = `panel-joint-${side > 0 ? 'r' : 'l'}`;
    band.castShadow = false;
    band.receiveShadow = true;
    g.add(band);
  }

  return g;
}

/**
 * The straw bed in the hospital pen, seeded so it is identical on every run.
 *
 * A bed that reshuffles between runs makes every screenshot in
 * `docs/verification/` unreproducible, which is most of what that folder is for.
 * `bornBay.ts` learned this and the seeding convention comes straight from it.
 *
 * Merged at a single placement rather than instanced: the clumps have different
 * geometry (different seeds) so they cannot be instanced, but they share three
 * surfaces so they can be merged. That is the difference between three draw calls
 * and forty-two.
 */
function strawBed(): THREE.Group {
  const bed = new THREE.Group();
  bed.name = 'straw-bed';
  const centreX = STRAW_PEN.side * (PEN_INNER_X + PEN_DEPTH / 2);
  // Deliberately not uniform: deeper toward the back of the pen, thinning toward
  // the mouth, because that is where it gets walked out of. Fourteen clumps over
  // 4.8 × 2.4 m is a bedded pen rather than a decorated one.
  const spots: [number, number][] = [
    [-2.15, -0.72], [-2.05, 0.15], [-1.8, 0.88], [-1.55, -0.35], [-1.5, 0.52],
    [-1.15, -0.92], [-1.05, 0.05], [-0.85, 0.72], [-0.62, -0.5], [-0.35, 0.35],
    [-0.28, -1.0], [0.05, 0.9], [0.12, -0.22], [0.38, 0.45], [0.45, -0.78],
    [0.72, 0.15], [0.8, 0.95], [0.95, -0.45], [1.15, 0.62], [1.3, -0.9],
    [1.45, 0.05], [1.62, 0.85], [1.75, -0.55], [1.95, 0.35], [2.1, -0.15],
    [-0.95, -0.05], [0.6, -0.05], [1.85, 0.6],
  ];
  for (const [i, spot] of spots.entries()) {
    const clump = strawClump(i * 13 + 5, zone.straw ?? 0xd0b578);
    clump.position.set(centreX + spot[0], DECK_Y, STRAW_PEN.z + spot[1]);
    clump.rotation.y = i * 0.9;
    bed.add(clump);
  }
  return instanceGroup(bed, [{ x: 0, z: 0 }], 'straw-bed');
}

/**
 * The pigs asleep in the pens, and there is nothing else to say about them.
 *
 * Four to a pen, heaped against the back wall, in the five pens each side that
 * are near enough to be resolved. **The pens beyond that are empty**, and that is
 * content rather than an optimisation that got a story attached to it afterwards:
 * a finishing house is filled and emptied in batches, and half a hall of empty
 * pens at night is a fact about where the last batch went. It happens to also be
 * ~9000 triangles nobody could have seen through the haze.
 *
 * They are merged, not instanced-and-animated. `FINISHER` ships a `breath` pivot
 * and a hall of them breathing slightly out of phase is a shot this project
 * should take — but a merged batch has no pivots left, and thirty-two separately
 * drawn animals is thirty-two times three draw calls. Filed against beat 4 in
 * `docs/backlog.md`, where there is a reason to pay for it.
 */
function sleepers(): THREE.Group {
  /** Where four pigs lie in a pen, as offsets from its inner corner. */
  const heap: { dx: number; dz: number; rotY: number }[] = [
    { dx: 3.15, dz: -0.58, rotY: 0.22 },
    { dx: 3.95, dz: -0.12, rotY: -0.14 },
    { dx: 3.3, dz: 0.46, rotY: 0.38 },
    { dx: 4.15, dz: 0.78, rotY: 0.06 },
  ];
  const placements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of penCentres()) {
      if (Math.abs(z) > PEN_PITCH * 2) continue;
      if (z === OTTO_PEN.z && side === OTTO_PEN.side) continue;
      if (z === STRAW_PEN.z && side === STRAW_PEN.side) continue;
      for (const [i, spot] of heap.entries()) {
        placements.push({
          x: side * (PEN_INNER_X + spot.dx),
          y: DECK_Y,
          z: z + spot.dz,
          // Mirrored on the left so both rows lie with their backs to the wall,
          // and offset per pig so the heap is a heap rather than a rack.
          rotY: side * (Math.PI / 2 + spot.rotY) + i * 0.03,
        });
      }
    }
  }
  return instanceGroup(assetOrThrow('finisher'), placements, 'sleepers');
}

/**
 * Otto, alone in his pen, and NOT part of the dressing.
 *
 * Everything else in this room is merged into an instanced batch, which is what
 * makes a hall this size affordable and which destroys every pivot in the source.
 * He keeps his, because the beat's one performance is his: `roll`, `breath` and
 * `head` are what `OttoDirector` drives. He costs his own draw calls and he is
 * worth them — he is the only thing in this building that is glad to see her.
 *
 * Returned rather than added, so `main.ts` owns his lifetime the same way it owns
 * the born bay's.
 */
export function createOtto(): THREE.Group {
  const otto = assetOrThrow('otto');
  otto.position.set(OTTO_AT.x, OTTO_AT.y, OTTO_AT.z);
  // **Facing the door she comes in by**, not down the hall.
  //
  // His pen is 2.40 m along Z and he is 2.35 m long, so he is aligned with Z or
  // he does not fit — the only choice left is which end his head is at, and it
  // is not a free one. She enters at the far −Z end and walks the alley toward
  // him, so a boar facing +Z would present her with a rump for the eleven metres
  // the approach takes, and the first character in the game who is glad to see
  // her would deliver that news to the back wall.
  otto.rotation.y = Math.PI;
  otto.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    }
  });
  const group = new THREE.Group();
  group.name = 'otto-pen';
  group.add(otto);
  return group;
}

/**
 * Otto did not need collision while the full wall kept Rosalie outside. Now the
 * box is read from his live mesh rather than authored: `OttoDirector` likewise
 * reads `#restRollY` from the rig after copied roll values dropped him through
 * the concrete twice during rebuilding. This standing-pose box only prevents
 * walking through him: the rendered roll sweeps beyond it. D-147's gate is the
 * full rendered sweep at Rosalie's occupied height, in `otto.spec.ts`.
 */
export function ottoBodySolid(otto: THREE.Object3D): HallSolid {
  otto.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(otto);
  const size = bounds.getSize(new THREE.Vector3());
  const centre = bounds.getCenter(new THREE.Vector3());
  return {
    name: 'otto-body',
    half: { x: size.x / 2, y: size.y / 2, z: size.z / 2 },
    pos: { x: centre.x, y: centre.y, z: centre.z },
    colour: CONCRETE_DARK,
    kind: 'organic',
    visible: false,
  };
}

/** The unchanged eight-tile deck under every ordinary pen, including Otto's return. */
export function fatteningSlatPlacements(): Placement[] {
  const placements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of penCentres()) {
      if (side === STRAW_PEN.side && z === STRAW_PEN.z) continue;
      for (const dz of [-0.6, 0.6]) {
        for (const dx of [0.6, 1.8, 3, 4.2]) placements.push({ x: side * (PEN_INNER_X + dx), z: z + dz });
      }
    }
  }
  return placements;
}

/** The normal hall swaps one intact tile for the matching broken threshold. */
export function fatteningBrokenSlatPlacements(options: FatteningHallOptions = {}): Placement[] {
  if (options.returnState) return [];
  return [{ x: PEN_INNER_X + 0.6 + BROKEN_DECK_REMAINDER_CENTER, z: -0.6 }];
}

/** Visible closed mouths share the exact return decision the collider list uses. */
export function fatteningPenWallPlacements(options: FatteningHallOptions = {}): Placement[] {
  const placements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of penCentres()) {
      if (side === STRAW_PEN.side && z === STRAW_PEN.z) continue;
      // In the normal hall Otto's mouth is the broken kit below. In return it is
      // already absent through `isFatteningMouthOpen`, so Beat 8's gate census
      // stays exactly as it was when the panel swung into the pen.
      if (side === OTTO_PEN.side && z === OTTO_PEN.z && !options.returnState) continue;
      if (isFatteningMouthOpen(options, side, z)) continue;
      placements.push({ x: side * PEN_WALL_X, z });
    }
    for (const z of dividerLines()) {
      for (const dx of [PEN_PITCH / 2, PEN_PITCH * 1.5]) {
        placements.push({ x: side * (PEN_INNER_X + dx), y: DECK_Y, z, rotY: Math.PI / 2 });
      }
    }
  }
  return placements;
}

/** The broken mouth exists only before Beat 8 opens Otto's ordinary gate. */
export function fatteningBrokenWallPlacements(options: FatteningHallOptions = {}): Placement[] {
  if (options.returnState) return [];
  return [{ x: OTTO_PEN.side * PEN_WALL_X, z: OTTO_PEN.z }];
}

/**
 * Dress the shell with the kit.
 *
 * Everything repeated goes through `instanceGroup` (D-024), which merges by
 * surface. This room places roughly 250 kit pieces; unmerged that is more draw
 * calls than spec §1 budgets for the entire frame, and merged it is about twenty.
 */
export function dressFatteningHall(options: FatteningHallOptions = {}): THREE.Group {
  const dressing = new THREE.Group();
  dressing.name = 'fattening-dressing';

  const pens = penCentres();
  const dividers = dividerLines();

  // Slatted floor over every pen except the hospital pen, which is concrete and
  // bedded. Four tiles across the 4.8 m depth, two along the 2.4 m pitch.
  const intactSlats = fatteningSlatPlacements().filter((placement) =>
    options.returnState || placement.x !== PEN_INNER_X + 0.6 || placement.z !== -0.6,
  );
  dressing.add(instanceGroup(assetOrThrow('slat-floor'), intactSlats, 'slat-floors'));
  dressing.add(instanceGroup(assetOrThrow('slat-floor-broken'), fatteningBrokenSlatPlacements(options), 'broken-otto-slats'));

  // ── the pen walls ──────────────────────────────────────────────────────────
  dressing.add(instanceGroup(assetOrThrow('pen-wall'), fatteningPenWallPlacements(options), 'pen-walls'));
  const brokenWalls = fatteningBrokenWallPlacements(options);
  if (brokenWalls.length > 0) {
    dressing.add(instanceGroup(assetOrThrow('pen-wall-broken'), brokenWalls, 'broken-pen-wall'));
    // The piece that came out, from `OTTO_CHUNK_AT` — the same transform
    // `fatteningSolids` collides, so the thing she walks round and the thing she
    // sees cannot become two objects. Guarded by the same list as the panel:
    // the returned hall has that mouth swung open and re-bedded, and rubble
    // lying in the alley two beats after somebody hosed the pen out would be a
    // room telling a story it has already finished telling.
    dressing.add(instanceGroup(
      assetOrThrow('pen-rubble'),
      [{ x: OTTO_CHUNK_AT.x, z: OTTO_CHUNK_AT.z, rotY: OTTO_CHUNK_AT.rotY }],
      'otto-rubble',
    ));
  }

  if (options.returnState) {
    dressing.add(instanceGroup(
      assetOrThrow('pen-wall'),
      [{ x: BLACKOUT_OTTO_GATE_AT.x, y: BLACKOUT_OTTO_GATE_AT.y, z: BLACKOUT_OTTO_GATE_AT.z, rotY: Math.PI / 2 }],
      'blackout-otto-open-gate',
    ));
    // Narrow irregular streaks sit over the unchanged drainage tiles. A full
    // dark plane would erase their gaps and turn a washed pen into wet asphalt.
    const wetStreak = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.006, 0.028), surface('mineral', CONCRETE_DARK));
    wetStreak.position.y = 0.003;
    dressing.add(instanceGroup(
      wetStreak,
      [
        { x: PEN_INNER_X + 0.78, y: DECK_Y, z: -0.62, rotY: 0.05 },
        { x: PEN_INNER_X + 1.72, y: DECK_Y, z: 0.54, rotY: -0.08 },
        { x: PEN_INNER_X + 2.65, y: DECK_Y, z: -0.42, rotY: 0.03 },
        { x: PEN_INNER_X + 3.56, y: DECK_Y, z: 0.74, rotY: -0.06 },
      ],
      'blackout-otto-wet-slats',
    ));
    dressing.add(instanceGroup(assetOrThrow('hose-reel'), [{ x: 1.32, y: DECK_Y, z: -0.86, rotY: -Math.PI / 2 }], 'blackout-otto-hose-reel'));
    dressing.add(instanceGroup(assetOrThrow('hose-nozzle'), [{ x: 1.20, y: DECK_Y, z: -0.48, rotY: -Math.PI / 2 }], 'blackout-otto-hose-nozzle'));
    dressing.add(instanceGroup(assetOrThrow('pen-card'), [{ x: 1.06, y: DECK_Y, z: 1.02, rotY: -Math.PI / 2 }], 'blackout-otto-pen-card'));
    // Unlike the surrounding static dressing this one kit keeps two pivots for
    // the fuse throw. Instancing would correctly save a draw but silently merge
    // those pivots away, leaving a story that advances in the dark with no lid.
    const fuseBox = assetOrThrow('fuse-box');
    fuseBox.name = 'blackout-fuse-box';
    fuseBox.position.set(BLACKOUT_FUSE_AT.x, BLACKOUT_FUSE_AT.y, BLACKOUT_FUSE_AT.z);
    fuseBox.rotation.y = -Math.PI / 2;
    dressing.add(fuseBox);

    // ── the crates, minus their lids ────────────────────────────────────────
    //
    // The lids come off here and are rebuilt as ONE instanced mesh by
    // `blackoutCrates.ts`, four of them never moving and two of them swinging.
    // Leaving them on would instance them at a baked closed transform, and a lid
    // baked into a merged geometry cannot open — which is how this would have
    // been discovered at the end of the beat rather than the start of it.
    const crate = assetOrThrow('hen-crate');
    crate.getObjectByName('hen-crate-lid-pivot')?.removeFromParent();
    dressing.add(instanceGroup(crate, blackoutCratePlacements(), 'blackout-hen-crates'));
  }

  /**
   * The lifted-out panel, leaning against the wall of the next pen along.
   *
   * **This is how the beat says "nobody is using this pen" without a word, a
   * prompt or a latch.** A gap in a wall is ambiguous; a gap with the missing
   * panel propped beside it is not. Beat 2 was the gate beat and this one must
   * not repeat it — she gets in because the way in is open, and the only thing
   * she has to work out is that it is.
   *
   * 22° off vertical, which is `asin(0.4 / 1.05)`: its foot stands in the alley
   * and its top edge rests on the top of the neighbouring wall.
   */
  dressing.add(
    instanceGroup(
      assetOrThrow('pen-wall'),
      [{
        x: STRAW_PEN.side * (FAT_HALL.alley - 0.35),
        z: STRAW_PEN.z - PEN_PITCH * 0.78,
        rotZ: STRAW_PEN.side * -0.39,
      }],
      'lifted-panel',
    ),
  );

  // Feed hoppers straddling the dividers, so one hopper feeds two pens. The
  // hospital pen gets none: a sick animal is hand-fed, which is the same reason
  // it has straw, and it usefully keeps the one pen she can walk into clear.
  const hopperPlacements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of dividers) {
      const strawSide = side === STRAW_PEN.side;
      if (strawSide && Math.abs(z - STRAW_PEN.z) < PEN_PITCH) continue;
      hopperPlacements.push({ x: side * (PEN_INNER_X + 0.45), y: DECK_Y, z });
    }
  }
  dressing.add(instanceGroup(assetOrThrow('feed-hopper'), hopperPlacements, 'feed-hoppers'));

  // The auger line the hoppers hang off, running the length of the hall over both
  // rows. Authored standing like every other asset, so it is turned over to be
  // mounted (Placement.rotX).
  const augerPlacements: Placement[] = [];
  for (const side of [-1, 1] as const) {
    for (const z of pens) {
      augerPlacements.push({ x: side * (PEN_INNER_X + 0.45), y: FAT_HALL.ceiling - 0.15, z, rotX: Math.PI });
    }
  }
  dressing.add(instanceGroup(assetOrThrow('pipe-run'), augerPlacements, 'auger-line'));

  // All five fittings, including the two that are switched off. See
  // `fatteningFixtures` for why only three of them have a light in them.
  dressing.add(
    instanceGroup(
      assetOrThrow('high-bay-lamp'),
      LAMP_Z.map((z) => ({ x: 0, y: FAT_HALL.ceiling, z, rotX: Math.PI })),
      'high-bays',
    ),
  );

  // Two fans in the far wall, facing back down the hall, where the noise is.
  dressing.add(
    instanceGroup(
      assetOrThrow('extractor-fan'),
      ([-1, 1] as const).map((side) => ({
        x: side * 2.6,
        y: 2.5,
        z: FAT_HALL.halfLength - 0.02,
        rotY: Math.PI,
      })),
      'extractors',
    ),
  );

  // The vent that breathes (spec §7 beat 3). Low in the far wall — low enough
  // that it is obviously pig-sized and obviously not Otto-sized, which is the
  // whole of what he has to say about it. **Nothing uses it this beat.** It is
  // beat 4's exit and it is filed there; what it does here is exist, so that when
  // he looks at it the player has already seen it.
  dressing.add(
    instanceGroup(
      assetOrThrow('wall-vent'),
      [{ x: VENT_AT.x, y: 0.34, z: VENT_AT.z, rotY: Math.PI }],
      'wall-vent',
    ),
  );

  // The door she came in by, and the curtain hanging in it.
  dressing.add(
    instanceGroup(assetOrThrow('hall-door'), [{ x: -2, z: -FAT_HALL.halfLength + 0.06 }], 'door'),
  );
  dressing.add(
    instanceGroup(assetOrThrow('strip-curtain'), [{ x: -2, z: -FAT_HALL.halfLength + 0.34 }], 'curtain'),
  );

  // The inspection lamp over the hospital pen. Authored standing like everything
  // else, so it is turned over to hang.
  dressing.add(
    instanceGroup(
      assetOrThrow('heat-lamp'),
      [{ x: INSPECTION_LAMP_AT.x, y: INSPECTION_LAMP_AT.y, z: INSPECTION_LAMP_AT.z, rotX: Math.PI }],
      'inspection-lamp',
    ),
  );

  dressing.add(sleepers());
  dressing.add(strawBed());

  return dressing;
}

/**
 * Build the hall shell and dress it.
 *
 * Shell first, kit second, exactly as the farrowing hall does it — so the room
 * can be built and looked at before a single kit piece exists, which is how both
 * of these rooms were developed.
 */
export function createFatteningHall(options: FatteningHallOptions = {}): THREE.Group {
  const room = new THREE.Group();
  room.name = 'fattening-hall';
  room.add(solidMeshes(fatteningSolids(options), 'shell'));
  room.add(structure());
  room.add(dressFatteningHall(options));
  return room;
}
